import React, {
  Fragment,
  forwardRef,
  useEffect,
  useRef,
  useState,
  Ref,
} from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  size,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react';
import { Listbox, Transition } from '@headlessui/react';
import cx from 'classnames';

import Portal from '../HOC/Portal';
import { ArrowDown, Check } from '../Icons';

export type DeskDrawerOptionType = {
  value: number | string;
  label?: string;
  disabled?: boolean;
};

export interface DeskDrawerProps {
  /**
   * The list options of desk drawer
   */
  options: DeskDrawerOptionType[];

  /**
   * The selected/active option
   */
  selected: DeskDrawerOptionType;

  /**
   * To add icon/logo on desk drawer
   */
  icon?: React.ReactNode;

  /**
   * The onChange handler
   */
  onChange?: (value: DeskDrawerOptionType) => void;

  children?: React.ReactNode;

  /**
   * To add additional className
   */
  className?: string;
}

interface DrawerOptionsProps {
  options: DeskDrawerOptionType[];
  className?: string;
  static?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const DeskDrawer = ({
  icon,
  options,
  selected,
  onChange = () => {},
  children,
  className,
}: DeskDrawerProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 5,
      }),
    ],
  });

  const refTrigger = useMergeRefs([refs.setReference, triggerRef]);
  const refFloating = useMergeRefs([refs.setFloating, popperRef]);

  const [selectedOption, setSelectedOption] =
    useState<DeskDrawerOptionType>(selected);

  useEffect(() => setSelectedOption(selected), [selected]);

  return (
    <Listbox
      as="div"
      className={cx('desk-drawer', className && className)}
      value={selectedOption}
      onChange={(value) => {
        setSelectedOption(value);
        onChange(value);
      }}
    >
      {({ open }) => (
        <>
          <Listbox.Button as={Fragment} ref={refTrigger}>
            <button className={cx({ 'md:bg-white md:shadow-lg': open })}>
              <div className="flex items-center">
                {icon && (
                  <a href="/" className="mr-4">
                    {icon}
                  </a>
                )}
                <p className="text-sm">
                  {selectedOption.label ?? selectedOption.value}
                </p>
              </div>
              <ArrowDown
                width={24}
                height={24}
                className="duration-200 ui-open:rotate-180 ml-1"
              />
            </button>
          </Listbox.Button>
          <DrawerOptions options={options} className="md:hidden">
            {children && children}
          </DrawerOptions>
          <Portal>
            <FloatingFocusManager context={context} modal={false}>
              <DrawerOptions
                options={options}
                className="max-md:hidden"
                ref={refFloating}
                static
                style={{
                  ...floatingStyles,
                  zIndex: 999,
                }}
              >
                {children && children}
              </DrawerOptions>
            </FloatingFocusManager>
          </Portal>
        </>
      )}
    </Listbox>
  );
};

const DrawerOptionList = (
  { options, className, children, ...rest }: DrawerOptionsProps,
  ref: Ref<HTMLElement>
) => (
  <Transition
    leave="transition ease-in duration-100"
    leaveFrom="opacity-100"
    leaveTo="opacity-0"
  >
    <Listbox.Options
      className={cx('desk-drawer-items-container', className && className)}
      ref={ref}
      {...rest}
    >
      {options.map((option) => (
        <Listbox.Option
          key={option.value}
          disabled={option.disabled}
          value={option}
          className="bg-primary md:bg-white text-sm"
        >
          {({ active, selected }) => (
            <div
              className={cx('desk-drawer-item', {
                '!bg-primary-selected': selected,
                'bg-primary bg-opacity-50': active,
              })}
            >
              <span
                className={cx(
                  'block truncate',
                  selected ? 'font-bold' : 'font-normal',
                  { 'opacity-30 md:opacity-50': option.disabled }
                )}
              >
                {option.label ?? option.value}
              </span>
              {selected && (
                <Check className="fill-uta-black" width={18} height={18} />
              )}
            </div>
          )}
        </Listbox.Option>
      ))}
      {children && children}
    </Listbox.Options>
  </Transition>
);

const DrawerOptions = forwardRef(DrawerOptionList);

export default DeskDrawer;
